import {BASE_URL} from "../../config/network";
import {ACCESSORIES_LOCATIONS_MOCK, ACCESSORIES_MOCK} from "../../mock/accessoriesMock";

const DEFAULT_HEADERS = {
    Accept: "application/json",
    "Content-Type": "application/json",
};

const VAT_DEFAULT = 21;

const shouldFallbackToMock = ({status, error}) => {
    if (error && !status) return true;
    return [404, 405, 500, 501, 502, 503, 504].includes(Number(status));
};

const toNumber = (value, fallback = 0) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
};

const toExcludingVat = (includingVat, vatPercentage = VAT_DEFAULT) => {
    const divider = 1 + toNumber(vatPercentage, VAT_DEFAULT) / 100;
    return toNumber(includingVat, 0) / divider;
};

const buildMockAccessoriesPayload = () => {
    const categoriesMap = new Map();
    const partnersMap = new Map();
    const locationMap = new Map();

    ACCESSORIES_LOCATIONS_MOCK.forEach((location, index) => {
        const id = toNumber(location.id, index + 1);
        locationMap.set(location.name, {
            id,
            name: location.name,
            landmark: null,
            partner: location.partner
                ? {
                    id: String(location.partner).toLowerCase().replace(/\s+/g, "-"),
                    name: location.partner
                }
                : null,
            address: {
                street: String(location.address || "").split(",")[0] || null,
                house_number: null,
                house_number_extension: null,
                city: String(location.address || "").split(",")[1]?.trim() || null,
                zip_code: location.postcode || null,
                country: "NL"
            },
            latitude: location.latitude,
            longitude: location.longitude,
            distance_km: location.distance_km
        });
    });

    const accessories = ACCESSORIES_MOCK.map((item, index) => {
        const categoryName = item.category || "Overig";
        const partnerName = item.partner || "KAV2GO";

        if (!categoriesMap.has(categoryName)) {
            categoriesMap.set(categoryName, {
                id: String(categoriesMap.size + 1),
                name: categoryName
            });
        }

        if (!partnersMap.has(partnerName)) {
            partnersMap.set(partnerName, {
                id: String(partnersMap.size + 1),
                name: partnerName
            });
        }

        const category = categoriesMap.get(categoryName);
        const subsidiary = partnersMap.get(partnerName);

        const rentalIncludingVat = item.canRent ? toNumber(item.rentPrice, 0) : 0;
        const purchaseIncludingVat = item.canBuy ? toNumber(item.buyPrice, 0) : 0;
        const depositIncludingVat = item.canRent ? toNumber(item.deposit, 0) : 0;

        const locations = (Array.isArray(item.locations) ? item.locations : [])
            .map((locationName) => locationMap.get(locationName))
            .filter(Boolean);

        return {
            id: toNumber(item.id, index + 1),
            name: item.name,
            description: item.description || "",
            image: item.image || null,
            category: {
                id: category.id,
                name: category.name,
                image: null
            },
            subsidiary: {
                id: subsidiary.id,
                name: subsidiary.name
            },
            prices: {
                vat_percentage: VAT_DEFAULT,
                rental_excluding_vat: toExcludingVat(rentalIncludingVat, VAT_DEFAULT),
                rental_including_vat: rentalIncludingVat,
                purchase_excluding_vat: toExcludingVat(purchaseIncludingVat, VAT_DEFAULT),
                purchase_including_vat: purchaseIncludingVat,
                deposit_excluding_vat: toExcludingVat(depositIncludingVat, VAT_DEFAULT),
                deposit_including_vat: depositIncludingVat
            },
            locations
        };
    });

    return {
        accessories,
        categories: Array.from(categoriesMap.values()),
        partners: Array.from(partnersMap.values()),
        locations: Array.from(locationMap.values()).map(({id, name}) => ({id, name}))
    };
};

const MOCK_PAYLOAD = buildMockAccessoriesPayload();

const filterMockAccessories = ({categoryId, locationId, partnerId} = {}) => {
    return MOCK_PAYLOAD.accessories.filter((item) => {
        const matchCategory = categoryId ? String(item.category?.id) === String(categoryId) : true;
        const matchPartner = partnerId ? String(item.subsidiary?.id) === String(partnerId) : true;
        const matchLocation = locationId
            ? (Array.isArray(item.locations) && item.locations.some((location) => String(location.id) === String(locationId)))
            : true;
        return matchCategory && matchPartner && matchLocation;
    });
};

const getAccessoriesMock = ({categoryId, locationId, partnerId} = {}) => {
    return filterMockAccessories({categoryId, locationId, partnerId});
};

const getCategoriesMock = ({locationId, partnerId} = {}) => {
    const filtered = filterMockAccessories({locationId, partnerId});
    const map = new Map();
    filtered.forEach((item) => {
        if (!item?.category?.id) return;
        map.set(String(item.category.id), {id: item.category.id, name: item.category.name});
    });
    return Array.from(map.values());
};

const getLocationsMock = ({categoryId, partnerId} = {}) => {
    const filtered = filterMockAccessories({categoryId, partnerId});
    const map = new Map();
    filtered.forEach((item) => {
        (Array.isArray(item.locations) ? item.locations : []).forEach((location) => {
            map.set(String(location.id), {id: location.id, name: location.name});
        });
    });
    return Array.from(map.values());
};

const getPartnersMock = ({categoryId, locationId} = {}) => {
    const filtered = filterMockAccessories({categoryId, locationId});
    const map = new Map();
    filtered.forEach((item) => {
        if (!item?.subsidiary?.id) return;
        map.set(String(item.subsidiary.id), {id: item.subsidiary.id, name: item.subsidiary.name});
    });
    return Array.from(map.values());
};

const buildQuery = (params = {}) => {
    const query = new URLSearchParams();

    Object.entries(params).forEach(([key, value]) => {
        if (value === null || value === undefined || value === "" || value === "all") return;
        query.append(key, String(value));
    });

    return query.toString();
};

const fetchJson = async (path, params = {}) => {
    const query = buildQuery(params);
    const url = `${BASE_URL}${path}${query ? `?${query}` : ""}`;
    try {
        const response = await fetch(url, {
            method: "GET",
            headers: DEFAULT_HEADERS,
        });

        if (!response.ok) {
            if (shouldFallbackToMock({status: response.status})) {
                if (path.endsWith("/categories")) return getCategoriesMock(params);
                if (path.endsWith("/locations")) return getLocationsMock(params);
                if (path.endsWith("/partners")) return getPartnersMock(params);
                return getAccessoriesMock(params);
            }
            throw new Error(`HTTP error: ${response.status}`);
        }

        return response.json();
    } catch (error) {
        if (shouldFallbackToMock({error})) {
            if (path.endsWith("/categories")) return getCategoriesMock(params);
            if (path.endsWith("/locations")) return getLocationsMock(params);
            if (path.endsWith("/partners")) return getPartnersMock(params);
            return getAccessoriesMock(params);
        }
        throw error;
    }
};

const getAccessoriesAPI = async ({categoryId, locationId, partnerId} = {}) =>
    fetchJson("/public/attributes/accessories", {categoryId, locationId, partnerId});

const getAccessoriesCategoriesAPI = async ({locationId, partnerId} = {}) =>
    fetchJson("/public/attributes/accessories/categories", {locationId, partnerId});

const getAccessoriesLocationsAPI = async ({categoryId, partnerId} = {}) =>
    fetchJson("/public/attributes/accessories/locations", {categoryId, partnerId});

const getAccessoriesPartnersAPI = async ({categoryId, locationId} = {}) =>
    fetchJson("/public/attributes/accessories/partners", {categoryId, locationId});

export {
    getAccessoriesAPI,
    getAccessoriesCategoriesAPI,
    getAccessoriesLocationsAPI,
    getAccessoriesPartnersAPI,
};
